import React, { useState, useEffect } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import {
  PawPrint,
  Camera,
  Plus,
  Activity,
  TrendingUp,
  Heart,
  Award,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import InsuranceAssistant from "../components/InsuranceAssistant";

const OverviewPage = ({ user, userRole }) => {
  const { themeClasses } = useTheme();
  const navigate = useNavigate();
  const [userStats, setUserStats] = useState(null);
  const [userPets, setUserPets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadUserStats();
      loadUserPets();
    }
  }, [user]);

  const loadUserStats = async () => {
    try {
      const getUserStats = httpsCallable(functions, "getUserStats");
      const result = await getUserStats();
      if (result.data.success) {
        setUserStats(result.data.stats);
      }
    } catch (error) {
      console.error("Failed to load user stats:", error);
    }
  };

  const loadUserPets = async () => {
    console.log("Loading pets for user:", user.uid);
    try {
      const getUserPets = httpsCallable(functions, "getUserPets");
      const result = await getUserPets();

      if (result.data.success) {
        setUserPets(result.data.pets);
        console.log(
          "Loaded",
          result.data.pets.length,
          "pets via Firebase Function"
        );
      }
    } catch (error) {
      console.error("Failed to load user pets:", error);
      setUserPets([]);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      title: "Identify Pet",
      description: "Use AI to identify any pet from a photo",
      icon: Camera,
      color: "from-blue-500 to-cyan-500",
      action: () => navigate("/dashboard/identify"),
    },
    {
      title: "Register Pet",
      description: "Add a new pet to your collection",
      icon: Plus,
      color: "from-green-500 to-emerald-500",
      action: () => navigate("/dashboard/register"),
    },
    {
      title: "My Pets",
      description: "View and manage your registered pets",
      icon: PawPrint,
      color: "from-purple-500 to-pink-500",
      action: () => navigate("/dashboard/pets"),
    },
    ...(userRole === "pro" || userRole === "admin"
      ? [
          {
            title: "Health Records",
            description: "Track your pets' medical history",
            icon: Activity,
            color: "from-red-500 to-orange-500",
            action: () => navigate("/dashboard/health"),
          },
        ]
      : []),
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div
            className={`h-8 ${themeClasses.bg.secondary} rounded w-1/4 mb-6`}
          ></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className={`h-32 ${themeClasses.bg.secondary} rounded-xl`}
              ></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2
        className={`text-xl lg:text-2xl font-bold ${themeClasses.text.primary}`}
      >
        Dashboard Overview
      </h2>

      {/* Stats Grid - Original Design */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div
          className={`${themeClasses.bg.card} ${themeClasses.border.primary} border rounded-xl p-6`}
        >
          <div className="flex items-center space-x-3">
            <PawPrint className={`w-8 h-8 ${themeClasses.text.accent}`} />
            <div>
              <p className={`text-sm ${themeClasses.text.secondary}`}>
                My Pets
              </p>
              <p className={`text-2xl font-bold ${themeClasses.text.primary}`}>
                {userPets.length}
              </p>
            </div>
          </div>
        </div>

        <div
          className={`${themeClasses.bg.card} ${themeClasses.border.primary} border rounded-xl p-6`}
        >
          <div className="flex items-center space-x-3">
            <Camera className={`w-8 h-8 ${themeClasses.text.accent}`} />
            <div>
              <p className={`text-sm ${themeClasses.text.secondary}`}>
                API Calls This Month
              </p>
              <p className={`text-2xl font-bold ${themeClasses.text.primary}`}>
                {userStats?.apiCallsThisMonth || 0}
              </p>
            </div>
          </div>
        </div>

        <div
          className={`${themeClasses.bg.card} ${themeClasses.border.primary} border rounded-xl p-6`}
        >
          <div className="flex items-center space-x-3">
            <Heart className={`w-8 h-8 ${themeClasses.text.accent}`} />
            <div>
              <p className={`text-sm ${themeClasses.text.secondary}`}>
                Health Records
              </p>
              <p className={`text-2xl font-bold ${themeClasses.text.primary}`}>
                {userStats?.healthRecordsCount || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2
          className={`text-xl font-semibold ${themeClasses.text.primary} mb-4 flex items-center`}
        >
          <TrendingUp className={`w-5 h-5 mr-2 ${themeClasses.text.accent}`} />
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {quickActions.map((action) => (
            <button
              key={action.title}
              onClick={action.action}
              className={`${themeClasses.bg.card} ${themeClasses.border.primary} border rounded-xl p-6 ${themeClasses.cardHover} transition-all duration-300 text-left group hover:scale-105`}
            >
              <div className="flex items-start space-x-4">
                <div
                  className={`p-3 rounded-lg bg-gradient-to-r ${action.color} group-hover:scale-110 transition-transform`}
                >
                  <action.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3
                    className={`font-semibold ${
                      themeClasses.text.primary
                    } group-hover:${
                      themeClasses.text.accent.split(" ")[1]
                    } transition-colors`}
                  >
                    {action.title}
                  </h3>
                  <p className={`text-sm mt-1 ${themeClasses.text.secondary}`}>
                    {action.description}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Account Level Card */}
      <div
        className={`${themeClasses.bg.card} ${themeClasses.border.primary} border rounded-xl p-6`}
      >
        <div className="flex items-center space-x-4">
          <div
            className={`p-3 rounded-lg bg-gradient-to-r ${
              userRole === "admin"
                ? "from-red-500 to-pink-500"
                : userRole === "pro"
                ? "from-purple-500 to-indigo-500"
                : "from-gray-500 to-gray-600"
            }`}
          >
            <Award className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className={`font-semibold ${themeClasses.text.primary}`}>
              Account Level: {userRole?.toUpperCase()}
            </h3>
            <p className={`text-sm ${themeClasses.text.secondary}`}>
              {userRole === "admin"
                ? "Full access to all features and admin controls"
                : userRole === "pro"
                ? "Premium features including health records and breed info"
                : "Basic features with pet registration and identification"}
            </p>
          </div>
          {userRole === "free" && (
            <button
              onClick={() => navigate("/dashboard/settings")}
              className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-white rounded-lg transition-all duration-300"
            >
              Upgrade
            </button>
          )}
        </div>
      </div>

      {/* Welcome Message for New Users */}
      {userPets.length === 0 && (
        <div
          className={`${themeClasses.bg.card} ${themeClasses.border.primary} border rounded-xl p-8 text-center`}
        >
          <PawPrint
            className={`w-16 h-16 ${themeClasses.text.accent} mx-auto mb-4`}
          />
          <h3 className={`text-xl font-bold ${themeClasses.text.primary} mb-2`}>
            Welcome to PetID.Neural!
          </h3>
          <p className={`mb-6 ${themeClasses.text.secondary}`}>
            Get started by registering your first pet or try our AI
            identification system.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/dashboard/register")}
              className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300"
            >
              <Plus className="w-5 h-5 mr-2 inline" />
              Register Your First Pet
            </button>
            <button
              onClick={() => navigate("/dashboard/identify")}
              className={`${themeClasses.bg.buttonSecondary} ${themeClasses.text.primary} font-semibold py-3 px-6 rounded-lg transition-colors`}
            >
              <Camera className="w-5 h-5 mr-2 inline" />
              Try AI Identification
            </button>
          </div>
        </div>
      )}

      {/* Insurance Assistant - Floating Widget */}
      <InsuranceAssistant userPets={userPets} />
    </div>
  );
};

export default OverviewPage;
